import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { meQuery } from './api/queries.js'
import { LoginPage } from './features/auth/LoginPage.js'

// Every screen except sign-in loads on demand.
const ConsoleLayout = lazyRouteComponent(() => import('./components/ConsoleLayout.js'), 'ConsoleLayout')
const DashboardPage = lazyRouteComponent(() => import('./features/dashboard/DashboardPage.js'), 'DashboardPage')
const SitesPage = lazyRouteComponent(() => import('./features/sites/SitesPage.js'), 'SitesPage')
const SitePage = lazyRouteComponent(() => import('./features/sites/SitePage.js'), 'SitePage')
const StaffPage = lazyRouteComponent(() => import('./features/staff/StaffPage.js'), 'StaffPage')
const PaymentsPage = lazyRouteComponent(() => import('./features/payments/PaymentsPage.js'), 'PaymentsPage')
const RequestsPage = lazyRouteComponent(() => import('./features/requests/RequestList.js'), 'RequestsPage')
const AuditPage = lazyRouteComponent(() => import('./features/audit/AuditPage.js'), 'AuditPage')
const ProfilePage = lazyRouteComponent(() => import('./features/auth/ProfilePage.js'), 'ProfilePage')

type RouterContext = { queryClient: QueryClient }

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet })

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: LoginPage,
})

/** Authenticated layout. The API is the real gate; this only decides what to render. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery)
    } catch {
      throw redirect({ to: '/login', search: { redirect: location.pathname } })
    }
  },
  component: ConsoleLayout,
})

const dashboardRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: DashboardPage })
const sitesRoute = createRoute({ getParentRoute: () => appRoute, path: '/sites', component: SitesPage })
const siteRoute = createRoute({ getParentRoute: () => appRoute, path: '/sites/$id', component: SitePage })
const staffRoute = createRoute({ getParentRoute: () => appRoute, path: '/staff', component: StaffPage })
const paymentsRoute = createRoute({ getParentRoute: () => appRoute, path: '/payments', component: PaymentsPage })
const requestsRoute = createRoute({ getParentRoute: () => appRoute, path: '/requests', component: RequestsPage })
const auditRoute = createRoute({ getParentRoute: () => appRoute, path: '/audit', component: AuditPage })
const profileRoute = createRoute({ getParentRoute: () => appRoute, path: '/profile', component: ProfilePage })

const routeTree = rootRoute.addChildren([loginRoute, appRoute.addChildren([dashboardRoute, sitesRoute, siteRoute, paymentsRoute, requestsRoute, staffRoute, auditRoute, profileRoute])])

export const createAppRouter = (queryClient: QueryClient) => createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
